import { Button, Text } from '@payaca/ambrosia';
import qs from 'qs';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { actions as appActions } from '@/api/app';

import useLogout from '@/hooks/auth/useLogout';
import LoginForm from '@/ui/components/loginForm/LoginForm';
import ResetPasswordForm from '@/ui/components/resetPasswordForm/ResetPasswordForm';

import './ResetPasswordPage.sass';

enum PageState {
  RESET_PASSWORD = 'reset-password',
  RESET_PASSWORD_SUCCESS = 'reset-password-success',
  INVALID_TOKEN = 'invalid-token',
}

type Props = {
  token?: string;

  match?: any;
  location?: any;
  history?: any;

  isAuthed?: boolean;
};

const ResetPasswordPage: FunctionComponent<Props> = ({
  token: propToken,
  match,
  location: propLocation,
  history: propHistory,
  isAuthed = false,
}: Props): JSX.Element => {
  const dispatch = useDispatch();

  // fallback to hooks if router props not provided
  const historyHook = useHistory();
  const locationHook = useLocation();

  const history = propHistory || historyHook;
  const location = propLocation || locationHook;

  const tokenFromMatch = match && match.params ? match.params.token : undefined;
  const token = propToken ?? tokenFromMatch ?? '';

  const [pageState, setPageState] = useState(PageState.RESET_PASSWORD);
  const [linkToNativeApp, setLinkToNativeApp] = useState(false);

  const logout = useLogout();

  useEffect(() => {
    if (isAuthed) {
      logout();
    } else {
      validateToken();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'validateToken'
  }, [isAuthed, logout]);

  const validateToken = useCallback(() => {
    const search =
      typeof location === 'string'
        ? location
        : (location && location.search) || '';

    const query = qs.parse(search, {
      ignoreQueryPrefix: true,
    });

    setLinkToNativeApp(query.linkToNativeApp === 'true');

    dispatch(
      appActions.validResetToken(token, (err: any) => {
        if (err) {
          setPageState(PageState.INVALID_TOKEN);
        }
      })
    );
  }, [dispatch, location, token]);

  return (
    <div>
      <div className="mb-4 flex flex-col gap-4">
        <h1 className="mb-0 text-4xl font-bold">
          {pageState === PageState.INVALID_TOKEN
            ? 'Oops!'
            : 'Set a new password'}
        </h1>
        <Text colour="muted">
          {pageState === PageState.INVALID_TOKEN
            ? 'The password reset link is invalid or has expired. Please request a new link.'
            : 'Choose a strong password to keep your account secure.'}
        </Text>
      </div>
      {pageState === PageState.RESET_PASSWORD && (
        <ResetPasswordForm
          resetToken={token}
          onSuccessCallback={() => history.push('/login')}
        />
      )}

      {pageState === PageState.INVALID_TOKEN && (
        <Button onClick={() => history.push('/login/forgotPassword')}>
          Request a new one
        </Button>
      )}

      {pageState === PageState.RESET_PASSWORD_SUCCESS && (
        <>
          {linkToNativeApp && (
            <a href={'Payaca://'} className="mobile-app-link">
              <Button>Return to login page</Button>
            </a>
          )}

          {!linkToNativeApp && <LoginForm hideForgotPassword={true} />}
        </>
      )}
    </div>
  );
};

export default ResetPasswordPage;
